import React from 'react';
import { useNavigate } from 'react-router-dom';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import { Trans } from 'react-i18next';
import Modal from 'Douleutaras/components/Modal/Modal';
import { CCFModalOptions } from 'Douleutaras/types';
import { AllModalOptions, ContactModalOptions } from 'Douleutaras/constants/ccf/need-help-options';
import { PhoneNumberRevealedSource } from 'Douleutaras/app/lib/posthog/posthog.types';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { usersSelector } from 'Douleutaras/redux/data/users/usersSlice';
import { useProGenderInfo } from 'Douleutaras/hooks/users/pro';
import { ContactModalContent } from 'Douleutaras/functional-components/ccf/ContactModalContent';
import { ExtraBidSource, useAB } from 'Douleutaras/app/lib/ab';
import { useJobExtraBidState } from 'Douleutaras/hooks/jobs/jobExtraBidState';

import { requestCcfExtraBid } from './lib/actions';

interface ContactModalProps {
  isOpen: boolean;
  conversationId: string;
  sellerPhoneNumber: string;
  title: React.ReactElement;
  note: string | React.ReactElement;
  options: CCFModalOptions;
  sellerId: string;
  userMugshot?: string;
  showBackButton?: boolean;
  jobId?: string;
  bidId?: string;
  source?: PhoneNumberRevealedSource;
  onBack?(): void;
  setIsOpen(modalStatus): void;
  onOptionSelected?(userSelection): void;
  onCCFModalOptionSelected?({ optionSlug }: { optionSlug: AllModalOptions }): void;
}

export const ContactModal = ({
  isOpen,
  setIsOpen,
  conversationId,
  sellerPhoneNumber,
  title,
  note,
  options,
  sellerId,
  onBack,
  onOptionSelected,
  showBackButton = false,
  jobId,
  bidId,
  source,
  onCCFModalOptionSelected
}: ContactModalProps) => {
  const { captureExtraBid } = useAB();
  const navigate = useNavigate();
  const sellerGenderInfo = useProGenderInfo(sellerId);
  const { bidsCount } = useJobExtraBidState(jobId);
  const seller = useAppSelector(state => usersSelector.selectById(state, sellerId));

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>
      <DefaultModal
        serviceIcon={PopupServiceImg}
        serviceIconAlt={<Trans>T.CCF.CONTACT IMAGE ALT TEXT</Trans>}
        title={React.cloneElement(title, { components: sellerGenderInfo })}
        note={note}
        backText={<Trans>T.CCF.CONTACT BACK BUTTON TEXT</Trans>}
        backTextAriaLabel={<Trans>T.CCF.CONTACT BACK BUTTON TEXT</Trans>}
        closeAriaLabel={<Trans>T.CCF.CONTACT CLOSE ICON LABEL</Trans>}
        closeButtonCallback={() => setIsOpen(false)}
        backButtonCallback={typeof onBack === 'function' ? onBack : () => false}
        showBackButton={showBackButton}
        userMugshot={seller?.attributes.mugshot}
      >
        <ContactModalContent hasChat={options?.[ContactModalOptions.I_WILL_USE_THE_CHAT]?.active}
                             hasPhone={options?.[ContactModalOptions.I_WILL_CALL_THE_PRO]?.active}
                             hasExtraPro={options?.[ContactModalOptions.I_WANT_EXTRA_PRO]?.active}
                             wantsToPayOnline={options?.[ContactModalOptions.I_WILL_SEND_ONLINE_PAYMENT]?.active}
                             wantsToPayOnlineDisabled={options?.[ContactModalOptions.I_WILL_SEND_ONLINE_PAYMENT]?.disabled as boolean}
                             conversationId={conversationId}
                             phoneNumber={sellerPhoneNumber}
                             userId={sellerId}
                             jobId={jobId}
                             bidId={bidId}
                             source={source}
                             onOptionSelected={async action => {
                               if (typeof onOptionSelected === 'function') {
                                 await onOptionSelected(action);
                               }
                               switch (action) {
                                 case ContactModalOptions.I_WILL_USE_THE_CHAT:
                                   setIsOpen(false);
                                   break;
                                 case ContactModalOptions.I_WILL_CALL_THE_PRO:
                                   break;
                                 case ContactModalOptions.I_WANT_EXTRA_PRO:
                                   // @AB PostHog event
                                   await requestCcfExtraBid({
                                     jobId,
                                     bidsCount,
                                     navigate,
                                     onOptionSelected: onCCFModalOptionSelected,
                                     eventTracking: captureExtraBid,
                                     eventValue: ExtraBidSource.CCF_NEED_HELP
                                   });
                                   break;
                                 default:
                                   break;
                               }
                             }}
        />
      </DefaultModal>
    </Modal>
  );
};

export default ContactModal;
